import { useEffect, useState } from 'react'
import { LuCheck, LuCopy, LuShare } from 'react-icons/lu'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { Z_INDEX } from '../constants'
import useClipboard from '../hooks/useClipboard'
import { nativeCall } from '../lib/nativeApp'

type ShareLinkButtonProps = {
  url: string
  /** What is shared, for the dialog and the share sheet: the service's label. */
  title: string
}

/**
 * A service's Share, in the noBGP app (mobile/): a QR code of the link, so a person beside you
 * scans it, with Copy and the phone's own share sheet. A browser keeps the copy button
 * (NodeServices.tsx).
 *
 * ⚠ The QR is drawn dark on WHITE in both colour schemes, with its quiet zone: a scanner reads a
 * light-on-dark code badly or not at all.
 */
export default function ShareLinkButton({ url, title }: Readonly<ShareLinkButtonProps>) {
  const [open, setOpen] = useState(false)
  const [svg, setSvg] = useState<string | null>(null)
  const { copy, copied } = useClipboard({ copiedTimeout: 1500 })

  useEffect(() => {
    if (!open) return
    // Loaded on first use: only the app shows this dialog, so the web bundle does not carry it.
    import('qrcode')
      .then(QRCode =>
        QRCode.toString(url, {
          type: 'svg',
          margin: 2,
          errorCorrectionLevel: 'M',
          color: { dark: '#000000', light: '#ffffff' },
        }),
      )
      .then(setSvg)
      .catch(() => setSvg(null))
  }, [open, url])

  const share = async () => {
    // The app's @capacitor/share opens the system share sheet. An older build of the app without
    // the plugin falls back to the Web Share API (iOS has it in the WebView; Android does not),
    // then to the clipboard.
    try {
      await nativeCall('Share', 'share', { title, url, dialogTitle: `Share ${title}` })
      return
    } catch (e) {
      // A cancel in the share sheet rejects too: that is the person's answer, not a missing plugin.
      if (/cancel/i.test(String((e as { message?: unknown } | null)?.message ?? ''))) return
    }
    if (typeof navigator.share === 'function') {
      await navigator.share({ title, url }).catch(() => undefined)
      return
    }
    await copy(url)
  }

  return (
    <>
      <IconButton
        size="small"
        aria-label={`Share ${title}`}
        sx={{ paddingX: 1, color: 'text.disabled' }}
        onClick={() => setOpen(true)}
      >
        <LuShare />
      </IconButton>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs" sx={{ zIndex: Z_INDEX.MODAL }}>
        <DialogTitle>Share {title}</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              mx: 'auto',
              width: '100%',
              maxWidth: 280,
              aspectRatio: '1',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: '#ffffff',
              '& svg': { display: 'block', width: '100%', height: '100%' },
            }}
            role="img"
            aria-label={`QR code for ${url}`}
            // The SVG is generated here from the link, by the qrcode library: no user markup.
            dangerouslySetInnerHTML={svg ? { __html: svg } : undefined}
          />
          <Typography
            variant="body2"
            sx={{ mt: 2, textAlign: 'center', color: 'text.secondary', wordBreak: 'break-all' }}
          >
            {url}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            fullWidth
            variant="outlined"
            color={copied ? 'success' : 'primary'}
            startIcon={copied ? <LuCheck /> : <LuCopy />}
            sx={{ justifyContent: 'center', gap: 1 }}
            onClick={() => void copy(url)}
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>
          <Button
            fullWidth
            variant="contained"
            startIcon={<LuShare />}
            sx={{ justifyContent: 'center', gap: 1 }}
            onClick={() => void share()}
          >
            Share
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
